import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FiArrowRight, FiCheckCircle, FiEye, FiEyeOff, FiGlobe, FiLock, FiMail, FiMapPin, FiPhone, FiUser } from 'react-icons/fi'
import { DContext } from '../../context/DataContext'
import Logo from '../../assets/Logo bg.svg'
import { translateAuthServerMessage } from '../../utils/authServerMessage'
import MetaData from '../../utils/MetaData'

const Register = () => {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { setIsAuth, setCurrentUser, BeURL, setLoader, isAuth } = useContext(DContext)

    const [form, setForm] = useState({
        fullname: '',
        countryCode: '',
        contactNumber: '',
        email: '',
        city: '',
        country: '',
        password: '',
        confirmPassword: ''
    })
    const [showPassword, setShowPassword] = useState(false)
    const [step, setStep] = useState('form')
    const [otp, setOtp] = useState('')
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (isAuth) {
            navigate('/', { replace: true })
        }
    }, [isAuth, navigate])

    useEffect(() => {
        setErrors({})
    }, [i18n.language])

    const passwordError = useMemo(() => {
        if (!form.password) return ''
        const password = form.password.trim()
        if (password.length < 8) return t('auth.validations.passwordMinLength')
        if (!/[A-Z]/.test(password)) return t('auth.validations.passwordUppercase')
        if (!/[a-z]/.test(password)) return t('auth.validations.passwordLowercase')
        if (!/\d/.test(password)) return t('auth.validations.passwordNumber')
        if (!/[!@#$%^&*]/.test(password)) return t('auth.validations.passwordSpecial')
        return ''
    }, [form.password, t])

    const confirmPasswordError = useMemo(() => {
        if (!form.confirmPassword) return ''
        return form.password === form.confirmPassword ? '' : t('auth.validations.passwordMismatch')
    }, [form.password, form.confirmPassword, t])

    const handleChange = (key, value) => {
        if (key === 'countryCode') {
            const digits = value.replace(/\D/g, '').slice(0, 4)
            const normalized = digits ? `+${digits}` : ''
            setForm((prev) => ({ ...prev, countryCode: normalized }))
            return
        }

        if (key === 'contactNumber') {
            const cleaned = value.replace(/\D/g, '').slice(0, 15)
            setForm((prev) => ({ ...prev, contactNumber: cleaned }))
            return
        }

        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const validateForm = () => {
        const nextErrors = {}
        if (!form.fullname.trim()) nextErrors.fullname = t('auth.validations.fullnameRequired')
        if (!form.email.trim()) nextErrors.email = t('auth.validations.emailRequired')
        if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) nextErrors.email = t('auth.validations.emailInvalid')
        if (!form.countryCode.trim()) nextErrors.countryCode = t('auth.validations.countryCodeRequired')
        if (form.countryCode.trim() && !/^\+\d{1,4}$/.test(form.countryCode.trim())) nextErrors.countryCode = t('auth.validations.countryCodeInvalid')
        if (!form.contactNumber.trim()) nextErrors.contactNumber = t('auth.validations.contactRequired')
        if (form.contactNumber.trim() && !/^\d{4,15}$/.test(form.contactNumber.trim())) nextErrors.contactNumber = t('auth.validations.contactInvalid')
        if (!form.city.trim()) nextErrors.city = t('auth.validations.cityRequired')
        if (!form.country.trim()) nextErrors.country = t('auth.validations.countryRequired')
        if (!form.password.trim()) nextErrors.password = t('auth.validations.passwordRequired')
        if (passwordError) nextErrors.password = passwordError
        if (!form.confirmPassword.trim()) nextErrors.confirmPassword = t('auth.validations.confirmPasswordRequired')
        if (confirmPasswordError) nextErrors.confirmPassword = confirmPasswordError
        return nextErrors
    }

    const handleSendOtp = async (e) => {
        e.preventDefault()

        const nextErrors = validateForm()
        setErrors(nextErrors)
        if (Object.keys(nextErrors).length > 0) return

        try {
            setLoader(true)
            const res = await fetch(`${BeURL}/register/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    fullname: form.fullname.trim(),
                    email: form.email.trim().toLowerCase(),
                    contact: { countryCode: form.countryCode.trim(), number: form.contactNumber.trim() },
                    password: form.password,
                    city: form.city.trim(),
                    country: form.country.trim()
                })
            })
            const data = await res.json()
            setLoader(false)

            if (!data.success) {
                alert(translateAuthServerMessage(data.message, t, 'auth.messages.otpSendFailed'))
                return
            }

            setStep('otp')
            alert(t('auth.messages.otpSent'))
        } catch (err) {
            setLoader(false)
            console.log('Error sending registration OTP:', err)
            alert(t('auth.messages.serverError'))
        }
    }

    const handleVerifyOtp = async (e) => {
        e.preventDefault()

        if (!otp.trim() || !/^\d{6}$/.test(otp.trim())) {
            setErrors({ otp: t('auth.validations.otpInvalid') })
            return
        }

        try {
            setLoader(true)
            const res = await fetch(`${BeURL}/register/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: form.email.trim().toLowerCase(), otp: otp.trim() })
            })
            const data = await res.json()
            setLoader(false)

            if (!data.success) {
                alert(translateAuthServerMessage(data.message, t, 'auth.messages.otpVerifyFailed'))
                return
            }

            setIsAuth(true)
            setCurrentUser(data.user)
            navigate('/', { replace: true })
        } catch (err) {
            setLoader(false)
            console.log('Error verifying registration OTP:', err)
            alert(t('auth.messages.serverError'))
        }
    }

    return (
        <>
            <MetaData
                title='Register | Be Alright Krabi'
                description='Create your Be Alright Krabi account to book tours and manage your trips.'
                canonicalUrl={`${window.location.origin}/register`}
                robots='noindex,nofollow'
            />
            <div className="relative flex min-h-[85vh] flex-wrap items-center justify-center gap-6 px-4 py-10">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(23,57,189,0.12),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.12),_transparent_45%)]" />

                <section className="relative w-full max-w-lg text-center">
                    <img src={Logo} alt="logo" className="mx-auto h-28 w-auto sm:h-36" />
                    <h1 className="mt-3 text-3xl font-black tracking-tight text-[#1739bd] sm:text-5xl">{t('auth.register.title')}</h1>
                    <p className="mt-3 text-sm text-slate-600 sm:text-base">{t('auth.register.subtitle')}</p>
                </section>

                <section className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.12)] backdrop-blur sm:p-6">
                    <h2 className="text-center text-2xl font-extrabold text-[#1739bd]">{t('auth.register.heading')}</h2>
                    <p className="mt-2 text-center text-sm text-slate-600">
                        {t('auth.register.haveAccount')}{' '}
                        <Link to="/login" className="font-semibold text-[#1739bd] no-underline hover:text-indigo-700">{t('auth.register.loginLink')}</Link>
                    </p>

                    {step === 'form' ? (
                        <form onSubmit={handleSendOtp} className="mt-5 space-y-3">
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.fullname')}</span>
                                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                    <FiUser className="text-slate-500" />
                                    <input value={form.fullname} onChange={(e) => handleChange('fullname', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="Subhin Krishna" />
                                </div>
                                {errors.fullname && <p className="mt-1 text-xs font-medium text-rose-600">{errors.fullname}</p>}
                            </label>

                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.contact')}</span>
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="col-span-1 flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                        <FiPhone className="text-slate-500" />
                                        <input value={form.countryCode} onChange={(e) => handleChange('countryCode', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="+66" />
                                    </div>
                                    <div className="col-span-2 flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                        <FiPhone className="text-slate-500" />
                                        <input value={form.contactNumber} onChange={(e) => handleChange('contactNumber', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="9876543210" />
                                    </div>
                                </div>
                                {(errors.countryCode || errors.contactNumber) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.countryCode || errors.contactNumber}</p>}
                            </label>

                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.email')}</span>
                                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                    <FiMail className="text-slate-500" />
                                    <input value={form.email} onChange={(e) => handleChange('email', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="name@mail.com" />
                                </div>
                                {errors.email && <p className="mt-1 text-xs font-medium text-rose-600">{errors.email}</p>}
                            </label>

                            <div className="grid grid-cols-2 gap-2">
                                <label className="block">
                                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.city')}</span>
                                    <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                        <FiMapPin className="text-slate-500" />
                                        <input value={form.city} onChange={(e) => handleChange('city', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="Krabi" />
                                    </div>
                                    {errors.city && <p className="mt-1 text-xs font-medium text-rose-600">{errors.city}</p>}
                                </label>

                                <label className="block">
                                    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.country')}</span>
                                    <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                        <FiGlobe className="text-slate-500" />
                                        <input value={form.country} onChange={(e) => handleChange('country', e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="Thailand" />
                                    </div>
                                    {errors.country && <p className="mt-1 text-xs font-medium text-rose-600">{errors.country}</p>}
                                </label>
                            </div>

                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.password')}</span>
                                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                    <FiLock className="text-slate-500" />
                                    <input value={form.password} onChange={(e) => handleChange('password', e.target.value)} type={showPassword ? 'text' : 'password'} className="w-full bg-transparent text-sm outline-none" placeholder="••••••••" />
                                    <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="text-slate-500 transition hover:text-[#1739bd]">
                                        {showPassword ? <FiEyeOff /> : <FiEye />}
                                    </button>
                                </div>
                                {(errors.password || passwordError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.password || passwordError}</p>}
                            </label>

                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.confirmPassword')}</span>
                                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                    <FiCheckCircle className="text-slate-500" />
                                    <input value={form.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} type={showPassword ? 'text' : 'password'} className="w-full bg-transparent text-sm outline-none" placeholder="••••••••" />
                                </div>
                                {(errors.confirmPassword || confirmPasswordError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.confirmPassword || confirmPasswordError}</p>}
                            </label>

                            <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
                                <span>{t('auth.register.sendOtp')}</span>
                                <FiArrowRight />
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="mt-5 space-y-4">
                            <p className="text-sm text-slate-600">{t('auth.register.otpSentTo', { email: form.email })}</p>
                            <label className="block">
                                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.otp')}</span>
                                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                                    <FiCheckCircle className="text-slate-500" />
                                    <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full bg-transparent text-sm outline-none" placeholder="123456" />
                                </div>
                                {errors.otp && <p className="mt-1 text-xs font-medium text-rose-600">{errors.otp}</p>}
                            </label>

                            <div className="flex gap-2">
                                <button type="button" onClick={() => setStep('form')} className="inline-flex w-1/2 items-center justify-center rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
                                    {t('auth.actions.back')}
                                </button>
                                <button type="submit" className="inline-flex w-1/2 items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
                                    <span>{t('auth.register.verifyOtp')}</span>
                                    <FiArrowRight />
                                </button>
                            </div>
                        </form>
                    )}
                </section>
            </div>
        </>
    )
}

export default Register